import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import GraphTopContent from '../Common/GraphTopContent';
import { NoRecordsFound, NoGraphsAvailable } from '../../NoRecordsFound';
import Loader from '../../../../components/Common/Loader';

import {
  getHeatmapData,
  unifyArrays as unifyData,
  setHeatmapXMargins,
  uniqueIdentViewsVariables,
} from './HeatmapHelpers';

import { getChartAxisLabel } from '../Common/HeatmapLineHelpers';

import useWindowsDimensions from '../../../../hooks/useWindowsDimensions';
import useHeatmapChart from '../../graphHooks/UseHeatmapChart';

import html2canvas from 'html2canvas';
import { userAppContext } from '../../../../context/Context';
import SharedButton from '@/global/Button/Button';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { HandRaisedIcon } from '@heroicons/react/24/outline';

function HeatmapTooltip({ cell, isSelected }) {
  const serieId = cell.serieId || cell.serie?.id || 'Unknown';
  const xValue = cell.data?.x || cell.x || 'Unknown';
  const yValue = cell.data?.y || cell.y || cell.value || 'Unknown';

  const tooltipId = `${serieId}, ${xValue}`;

  let relatedGenes = 'None';
  const genesData = cell.data?.genes || cell.genes || '';

  if (genesData) {
    if (Array.isArray(genesData)) {
      relatedGenes = genesData.join(', ');
    } else if (typeof genesData === 'string' && genesData.trim()) {
      relatedGenes = genesData;
    }
  }

  const variableNameMap = new Map([
    ['score', 'Score'],
    ['numPMIDsupportingAssociation', 'N. Publications'],
    ['EI', 'Evidence Index'],
    ['jaccard_genes', 'Jacccard Index Genes'],
    ['jaccard_variants', 'Jacccard Index Variants'],
    ['shared_genes', 'N. Shared Genes'],
    ['shared_variants', 'N. Shared Variants'],
  ]);

  const chartVar = cell.data?.chartVar || cell.chartVar || 'score';
  const variableName = variableNameMap.get(chartVar) || chartVar;
  const formattedValue = cell.formattedValue || yValue;

  return (
    <div className="custom-tooltip-heatmap">
      <div style={{ backgroundColor: cell.color || '#ccc' }} className="score-color"></div>
      <span>
        <em>{tooltipId}</em>
      </span>
      <br />
      <span>
        <em>{variableName}:</em> <strong>{formattedValue}</strong>
      </span>
      <br />
      {relatedGenes && relatedGenes !== 'None' && (
        <>
          <span>
            <em>Gene:</em> <strong>{relatedGenes}</strong>
          </span>
          <br />
        </>
      )}
      <span>
        <em>Click:</em> <strong>{isSelected ? 'Focused' : 'Focus this cell'}</strong>
      </span>
    </div>
  );
}

function HeatmapChart({
  source,
  keySearch,
  view,
  names,
  chartInfo,
  setChartInfo,
  baseUrl,
  parent,
  heatmapSearchVariables,
}: {
  source: any;
  keySearch: any;
  view: any;
  names: any;
  chartInfo: any;
  setChartInfo: any;
  baseUrl: any;
  parent: any;
  heatmapSearchVariables: any;
}) {
  const { restrictGridResultCount } = userAppContext();
  const defaultPageSize = restrictGridResultCount || (parent === 'DDA' ? 50 : 100);
  const sampleSizeOptions = [20, 50, 100, 250, 'ALL'];
  const [sampleSize, setSampleSize] = useState<number | 'ALL'>(defaultPageSize);
  const requestPageSize = sampleSize === 'ALL' ? -1 : sampleSize;
  const fetchAll = sampleSize === 'ALL';
  const [showPanningDropdown, setShowPanningDropdown] = useState(false);
  const panningDropdownRef = useRef<HTMLDivElement>(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);

  const isTopAxisDiseases =
    (parent === 'GDA' && view === 'GENES') || (parent === 'VDA' && view === 'VARIANTS') || parent === 'DDA';

  // current width
  const { width } = useWindowsDimensions();

  // empty array as the initial data for the chart
  const initialChartData = [];

  // calling the useRequestMultipleFilters hook
  const {
    chartData,
    isLoading,
    isPayloadEmpty,
    legendValues,
    height: heatmapHeight,
    identReqParam,
    xMargin,
    setXMargin,
    totalResults,
  } = useHeatmapChart({
    chartInfo,
    initialChartData,
    keySearch,
    source,
    baseUrl,
    parent,
    pageSize: requestPageSize,
    view,
    getHeatmapData,
    unifyData,
    width,
    isTopAxisDiseases,
    uniqueIdentViewsVariables,
    names,
  });

  const isChartDataIncomplete = chartData.length === 0 ? false : chartData.length === 1 ? true : false;
  const isChartDataEmpty = chartData.length === 0 ? true : false;
  const visibleResults = fetchAll
    ? totalResults || requestPageSize
    : Math.min(requestPageSize, totalResults || requestPageSize);
  const allColumns = useMemo(() => {
    const allValues = chartData.flatMap(row => row.data.map(cell => cell.x));
    return Array.from(new Set(allValues));
  }, [chartData]);
  const maxColumnIndex = Math.max(allColumns.length - 1, 0);
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(0);
  const [selectedCell, setSelectedCell] = useState<{ row: string; column: string } | null>(null);
  const [selectedCells, setSelectedCells] = useState<
    Array<{ row: string; column: string; value: string | number; rowIdent: string; columnIdent: string }>
  >([]);

  useEffect(() => {
    const defaultEnd = Math.min(maxColumnIndex, 39);
    setRangeStart(0);
    setRangeEnd(defaultEnd);
    setSelectedCell(null);
    setSelectedCells([]);
  }, [maxColumnIndex, chartInfo.variable]);

  const visibleColumns = useMemo(
    () => allColumns.slice(rangeStart, rangeEnd + 1),
    [allColumns, rangeStart, rangeEnd],
  );
  const visibleColumnSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const filteredChartData = useMemo(
    () =>
      chartData
        .map(row => ({
          ...row,
          data: row.data.filter(cell => visibleColumnSet.has(cell.x)),
        }))
        .filter(row => row.data.length > 0),
    [chartData, visibleColumnSet],
  );

  // adapt the top margin depending on whether the top axis represents diseases or not
  const topMargin = isTopAxisDiseases ? 180 : 110;

  // array of the types of value represented in the heatmap
  const comparedValues = uniqueIdentViewsVariables[parent]?.comparedValues?.[view];

  // adjust the X margins when the width changes
  useEffect(() => {
    if (identReqParam) {
      setXMargin(() => setHeatmapXMargins(width, identReqParam.split(',')));
    }
  }, [width, identReqParam, setXMargin]);

  function downloadJSON() {
    const JSONcomplete = JSON.stringify(chartData);
    const jsonURL = `data:text/json;charset=utf-8,${encodeURIComponent(JSONcomplete)}`;

    const link = document.createElement('a');
    link.href = jsonURL;
    link.download = 'HEATMAP.JSON';
    link.click();
  }

  const downloadPNG = () => {
    const chartContainer = document.querySelector('.row-graph') as HTMLElement;
    if (chartContainer) {
      html2canvas(chartContainer).then(function (canvas) {
        const link = document.createElement('a');
        link.download = 'chart.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    }
  };

  const panRange = (direction: 'left' | 'right') => {
    const windowSize = rangeEnd - rangeStart + 1;
    if (windowSize <= 0 || allColumns.length <= windowSize) return;
    const step = Math.max(Math.floor(windowSize / 3), 1);
    if (direction === 'left') {
      const nextStart = Math.max(0, rangeStart - step);
      setRangeStart(nextStart);
      setRangeEnd(nextStart + windowSize - 1);
    } else {
      const nextEnd = Math.min(maxColumnIndex, rangeEnd + step);
      setRangeEnd(nextEnd);
      setRangeStart(nextEnd - windowSize + 1);
    }
  };

  return (
    <section className="mb-8">
      <div className="container-fluid px-0">
        {isLoading && (
          <div className="row">
            {' '}
            <Loader />
          </div>
        )}
        {!isPayloadEmpty && !isLoading && (
          <Fragment>
            {!isChartDataIncomplete && !isChartDataEmpty && (
              <Fragment>
                <GraphTopContent
                  names={names}
                  view={view}
                  graphSearchVariables={heatmapSearchVariables}
                  chartInfo={chartInfo}
                  setChartInfo={setChartInfo}
                  parent={parent}
                  resultsNumber={visibleResults}
                  selectedSampleSize={sampleSize}
                  onSampleSizeChange={setSampleSize}
                  sampleSizeOptions={sampleSizeOptions}
                />
                <div className="row items-center justify-between">
                  <div className="graph-interactions">
                    <div style={{ position: 'relative' }} ref={panningDropdownRef}>
                      <SharedButton
                        variant="secondaryOutline"
                        onClick={() => {
                          setShowPanningDropdown(prev => !prev);
                        }}
                        className="mx-0"
                      >
                        <HandRaisedIcon className="w-5 mr-2" />
                        Panning
                        <ChevronDownIcon
                          className="w-4 ml-2 transition-transform duration-200"
                          style={{ transform: showPanningDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                      </SharedButton>
                      {showPanningDropdown && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md z-50 p-2 min-w-max flex flex-col items-center w-full">
                          <span
                            className="cursor-pointer row items-center justify-center font-bold p-2 rounded-md text-blue w-full hover:bg-blue-100"
                            onClick={() => panRange('left')}
                          >
                            Left Pan
                          </span>
                          <span
                            className="cursor-pointer row items-center justify-center font-bold p-2 rounded-md text-blue w-full hover:bg-blue-100"
                            onClick={() => panRange('right')}
                          >
                            Right Pan
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="graph-range-controls">
                      <span className="graph-range-label">Visible columns:</span>
                      <div className="row items-center">
                        <input
                          type="range"
                          min={0}
                          max={maxColumnIndex}
                          value={rangeStart}
                          onChange={e => {
                            const next = Number(e.target.value);
                            setRangeStart(Math.min(next, rangeEnd));
                          }}
                        />
                        <input
                          type="range"
                          min={0}
                          max={maxColumnIndex}
                          value={rangeEnd}
                          onChange={e => {
                            const next = Number(e.target.value);
                            setRangeEnd(Math.max(next, rangeStart));
                          }}
                        />
                        <span className="graph-range-window ml-2">
                          {rangeStart + 1}-{rangeEnd + 1} / {allColumns.length}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ position: 'relative' }} ref={actionsDropdownRef}>
                    <SharedButton
                      variant="secondaryOutline"
                      onClick={() => {
                        setShowActionsDropdown(prev => !prev);
                      }}
                      className="mr-0"
                    >
                      <ArrowDownTrayIcon className="w-5 mr-2" />
                      Download
                      <ChevronDownIcon
                        className="w-4 ml-2 transition-transform duration-200"
                        style={{ transform: showActionsDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </SharedButton>
                    {showActionsDropdown && (
                      <div className="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md z-50 p-2 min-w-max flex flex-col items-center w-full">
                        <span
                          className="cursor-pointer row items-center justify-center hover:bg-blue-100 font-bold p-2 rounded-md text-blue w-full"
                          onClick={() => {
                            downloadJSON();
                            setShowActionsDropdown(false);
                          }}
                        >
                          JSON
                        </span>
                        <span
                          className="cursor-pointer row items-center justify-center hover:bg-blue-100 font-bold p-2 rounded-md text-blue w-full"
                          onClick={() => {
                            downloadPNG();
                            setShowActionsDropdown(false);
                          }}
                        >
                          PNG
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`row-graph`}
                  style={{
                    minHeight: heatmapHeight,
                    height: heatmapHeight,
                    maxHeight: heatmapHeight > 3000 ? '3000px' : heatmapHeight,
                    overflowY: heatmapHeight > 3000 ? 'auto' : 'visible',
                    overflowX: 'auto',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%',
                    margin: '0 auto',
                  }}
                >
                  <ResponsiveHeatMap
                    data={filteredChartData}
                    margin={{
                      top: topMargin,
                      right: Math.max(xMargin + 60, 150),
                      bottom: 80,
                      left: Math.max(xMargin - 30, 80),
                    }}
                    enableLabels={true}
                    label={(cell: any) => {
                      const value = cell?.data?.y ?? cell?.value;
                      if (value === null || value === undefined || Number.isNaN(value)) return '';
                      return typeof value === 'number' ? value.toFixed(2) : String(value);
                    }}
                    labelTextColor={(cell: any) => {
                      const value = cell?.data?.y ?? cell?.value;
                      if (value === null || value === undefined || Number.isNaN(value)) return '#0f172a';
                      const min = Number(legendValues?.min ?? 0);
                      const max = Number(legendValues?.max ?? 1);
                      const ratio = (Number(value) - min) / (max - min || 1);
                      return ratio >= 0.58 ? '#ffffff' : '#0f172a';
                    }}
                    forceSquare={false}
                    axisTop={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -80,
                      legend: '',
                      legendOffset: -65,
                      renderTick: tickProperties => {
                        const labelClassNames = 'axisLegend verticalTopLegend';
                        const sliceValue = 21;
                        return getChartAxisLabel({
                          ...tickProperties,
                          labelClassNames,
                          sliceValue,
                          opacity: tickProperties.opacity || 1,
                        });
                      },
                    }}
                    axisRight={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: '',
                      renderTick: tickProperties => {
                        const labelClassNames = 'axisLegend';
                        const sliceValue = 21;
                        return getChartAxisLabel({
                          ...tickProperties,
                          labelClassNames,
                          sliceValue,
                          opacity: tickProperties.opacity || 1,
                        });
                      },
                    }}
                    axisLeft={null}
                    axisBottom={null}
                    colors={{
                      type: 'diverging',
                      scheme: 'blues',
                      divergeAt: legendValues.divergeAt,
                      minValue: legendValues.min,
                      maxValue: legendValues.max,
                    }}
                    emptyColor="#555555"
                    onClick={(cell: any) => {
                      const row = String(cell?.serieId || '');
                      const column = String(cell?.data?.x || cell?.x || '');
                      const value = cell?.data?.y ?? cell?.value ?? '';
                      const rowIdent = String(cell?.data?.rowIdent || '');
                      const columnIdent = String(cell?.data?.xIdent || '');
                      if (!row || !column) return;
                      const key = `${row}__${column}`;
                      setSelectedCells(prev => {
                        const exists = prev.some(item => `${item.row}__${item.column}` === key);
                        if (exists) {
                          return prev.filter(item => `${item.row}__${item.column}` !== key);
                        }
                        return [...prev, { row, column, value, rowIdent, columnIdent }];
                      });
                      setSelectedCell({ row, column });
                    }}
                    borderWidth={1}
                    borderColor={(cell: any) => {
                      const row = String(cell?.serieId || '');
                      const column = String(cell?.data?.x || cell?.x || '');
                      const isSelected = selectedCells.some(
                        item => item.row === row && item.column === column,
                      );
                      return isSelected ? '#be0080' : 'rgba(255,255,255,0.35)';
                    }}
                    tooltip={(tooltipProps: any) => {
                      const cell = tooltipProps?.cell || tooltipProps;
                      const row = String(cell?.serieId || '');
                      const column = String(cell?.data?.x || cell?.x || '');
                      const isSelected =
                        !!selectedCell && selectedCell.row === row && selectedCell.column === column;
                      return <HeatmapTooltip cell={cell} isSelected={isSelected} />;
                    }}
                    legends={[]}
                  />
                </div>
                <div className="heatmap-custom-legend" aria-label="Heatmap legend">
                  <span className="heatmap-custom-legend-value">{legendValues.min}</span>
                  <div className="heatmap-custom-legend-gradient" />
                  <span className="heatmap-custom-legend-value">{legendValues.max}</span>
                </div>
                <div className="additional-notes">
                  <div>
                    <p className="centered-paragraph">
                      <b>NOTE:</b> White spaces represent no association between the {comparedValues[0]} and
                      the {comparedValues[1]}
                    </p>
                    {parent === 'VDA' && (
                      <div>
                        <p className="centered-paragraph">
                          <b>IMPORTANT:</b> You can view the genes associated to the variant in the tooltip of
                          each cell
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Fragment>
            )}
            {isChartDataEmpty && (
              <Fragment>
                <GraphTopContent
                  graphSearchVariables={heatmapSearchVariables}
                  chartInfo={chartInfo}
                  setChartInfo={setChartInfo}
                  parent={parent}
                  resultsNumber={0}
                  selectedSampleSize={sampleSize}
                  onSampleSizeChange={setSampleSize}
                  sampleSizeOptions={sampleSizeOptions}
                />
                <NoGraphsAvailable />
              </Fragment>
            )}
            {isChartDataIncomplete && (
              <>
                <GraphTopContent
                  graphSearchVariables={heatmapSearchVariables}
                  chartInfo={chartInfo}
                  setChartInfo={setChartInfo}
                  parent={parent}
                  resultsNumber={0}
                  selectedSampleSize={sampleSize}
                  onSampleSizeChange={setSampleSize}
                  sampleSizeOptions={sampleSizeOptions}
                />
                <section className="col-12 no-records mb-8">
                  <h1>Heatmap is not possible with current data selection</h1>
                  <p>
                    The top {fetchAll ? totalResults || 'all' : requestPageSize} {chartInfo?.variableFullName}{' '}
                    correspond to {chartData?.[0]?.id} and do not coincide with other selected Diseases
                  </p>
                </section>
              </>
            )}
          </Fragment>
        )}
        {isPayloadEmpty && !isLoading && (
          <div className="row">
            {' '}
            <NoRecordsFound />
          </div>
        )}
      </div>
    </section>
  );
}

export default HeatmapChart;
